import dayjs from "dayjs";
import type { Priority, Severity, TaskStatus } from "../../../types/interfaces";

/** Minutes -> "45m", "3h 20m", "2d 4h". null/undefined/NaN -> "—". */
export function formatDuration(min: number | null | undefined): string {
  if (min === null || min === undefined || !Number.isFinite(min)) return "—";
  const m = Math.max(0, Math.round(min));
  if (m < 60) return `${m}m`;
  if (m < 1440) {
    const h = Math.floor(m / 60);
    const r = m % 60;
    return r ? `${h}h ${r}m` : `${h}h`;
  }
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  return h ? `${d}d ${h}h` : `${d}d`;
}

/** Minutes -> "12.5h" (one decimal). */
export function formatHours(min: number | null | undefined): string {
  if (min === null || min === undefined || !Number.isFinite(min)) return "—";
  return `${(Math.max(0, min) / 60).toFixed(1)}h`;
}

export interface Segment {
  key: string;
  label: string;
  value: number;
  color: string;
}

export interface PlacedSegment extends Segment {
  /** share of this bar's own total, 0-100 */
  pct: number;
}

export function sumCounts(counts: Partial<Record<string, number>>): number {
  return Object.values(counts).reduce<number>((n, v) => n + (v || 0), 0);
}

/** Build ordered segments from a counts map; zero-value entries are dropped. */
export function buildSegments(
  counts: Partial<Record<string, number>>,
  order: readonly string[],
  labels: Record<string, string>,
  colors: Record<string, string>
): Segment[] {
  return order
    .map((key) => ({ key, label: labels[key] ?? key, value: counts[key] || 0, color: colors[key] }))
    .filter((s) => s.value > 0);
}

/** Percentages for a stacked bar. Empty when the total is 0. */
export function placeSegments(segments: Segment[]): PlacedSegment[] {
  const total = segments.reduce((n, s) => n + s.value, 0);
  if (total <= 0) return [];
  return segments.filter((s) => s.value > 0).map((s) => ({ ...s, pct: (s.value / total) * 100 }));
}

/** Width (0-100) of a bar with `total` when the longest bar in the list has `max`. */
export function barWidth(total: number, max: number): number {
  if (max <= 0 || total <= 0) return 0;
  return Math.min(100, (total / max) * 100);
}

/** { from, to } as YYYY-MM-DD for a "last N days" window ending today. */
export function rangeFor(days: number, now: Date = new Date()): { from: string; to: string } {
  return {
    from: dayjs(now).subtract(days, "day").format("YYYY-MM-DD"),
    to: dayjs(now).format("YYYY-MM-DD"),
  };
}

/** Round a chart maximum up to a tidy 1/2/5 x 10^k value (min 4). */
export function niceMax(v: number): number {
  if (!Number.isFinite(v) || v <= 4) return 4;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * pow;
}

/** x/y SVG coordinates for a series. A single value sits in the middle. */
export function linePoints(
  values: number[],
  width: number,
  height: number,
  pad: { l: number; r: number; t: number; b: number },
  max: number
): { x: number; y: number }[] {
  const plotW = width - pad.l - pad.r;
  const plotH = height - pad.t - pad.b;
  return values.map((v, i) => ({
    x: pad.l + (values.length === 1 ? plotW / 2 : (i / (values.length - 1)) * plotW),
    y: pad.t + plotH - (max > 0 ? Math.min(v / max, 1) : 0) * plotH,
  }));
}

/** Left/width (% of the track) for a date span inside a domain; clamped. */
export function ganttPosition(
  start: string | Date,
  end: string | Date,
  domainStart: number,
  domainEnd: number
): { left: number; width: number } {
  const span = Math.max(domainEnd - domainStart, 1);
  const s = Math.min(Math.max(+new Date(start), domainStart), domainEnd);
  const e = Math.min(Math.max(+new Date(end), domainStart), domainEnd);
  const left = ((s - domainStart) / span) * 100;
  const width = Math.max(((Math.max(e, s) - s) / span) * 100, 0.8);
  return { left, width: Math.min(width, 100 - left) };
}

/** Position (%) of a single instant (the "today" marker); null when outside the domain. */
export function ganttMarker(at: number, domainStart: number, domainEnd: number): number | null {
  if (at < domainStart || at > domainEnd) return null;
  return ((at - domainStart) / Math.max(domainEnd - domainStart, 1)) * 100;
}

/* ------------------------------------------------------------ labels/colours */

export const STATUS_ORDER: TaskStatus[] = ["backlog", "ready", "in_progress", "in_review", "done"];
export const SEVERITY_ORDER: Severity[] = ["critical", "major", "minor", "trivial"];
export const PRIORITY_ORDER: Priority[] = ["urgent", "high", "medium", "low"];

export const STATUS_LABEL: Record<TaskStatus, string> = {
  backlog: "Backlog", ready: "Ready", in_progress: "In progress", in_review: "In review", done: "Done",
};
export const SEVERITY_LABEL: Record<Severity, string> = {
  critical: "Critical", major: "Major", minor: "Minor", trivial: "Trivial",
};
export const PRIORITY_LABEL: Record<Priority, string> = {
  urgent: "Urgent", high: "High", medium: "Medium", low: "Low",
};

export const STATUS_COLOR: Record<TaskStatus, string> = {
  backlog: "var(--osi-sand)",
  ready: "var(--osi-brand-300)",
  in_progress: "var(--osi-brand-500)",
  in_review: "var(--osi-warn)",
  done: "var(--osi-ok)",
};
export const SEVERITY_COLOR: Record<Severity, string> = {
  critical: "var(--osi-danger)",
  major: "var(--osi-warn)",
  minor: "var(--osi-brand-500)",
  trivial: "var(--osi-brand-300)",
};
export const PRIORITY_COLOR: Record<Priority, string> = {
  urgent: "var(--osi-danger)",
  high: "var(--osi-warn)",
  medium: "var(--osi-brand-500)",
  low: "var(--osi-brand-300)",
};

export type DashTone = "neutral" | "brand" | "info" | "warn" | "ok" | "danger";
export const SEVERITY_TONE: Record<Severity, DashTone> = {
  critical: "danger", major: "warn", minor: "info", trivial: "neutral",
};
export const PRIORITY_TONE: Record<Priority, DashTone> = {
  urgent: "danger", high: "warn", medium: "info", low: "neutral",
};

export const formatDate = (d?: string | null): string => (d ? dayjs(d).format("D MMM YYYY") : "—");
